import { useId } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";

interface ChartSpec {
  chart_type: "bar" | "line" | "area" | "pie" | "radar" | "mixed";
  x_field: string;
  series: Array<{
    data_key: string;
    label?: string;
    type?: "bar" | "line" | "area";
    axis?: "primary" | "secondary";
    color?: string;
    value_format?: string;
  }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  data: any[];
  title?: string;
  description?: string;
  value_format?: string;
}

interface ChartRendererProps {
  chartSpec: ChartSpec;
  hideTitle?: boolean;
  fillHeight?: boolean;
}

const COLORS = [
  "#2563eb",
  "#0f766e",
  "#d97706",
  "#64748b",
  "#0891b2",
  "#16a34a",
  "#ea580c",
  "#be123c",
];

const AXIS_TICK_STYLE = { fontSize: 12, fill: "#64748b" };
const GRID_COLOR = "#e2e8f0";
const TOOLTIP_STYLE = {
  backgroundColor: "rgba(255, 255, 255, 0.98)",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
  padding: "10px 12px",
};

const roundChartNumber = (value: number) => Math.round((value + Number.EPSILON) * 1_000_000) / 1_000_000;

const isValidColor = (value?: string) => {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith("#")) return true;
  if (trimmed.startsWith("rgb(") || trimmed.startsWith("rgba(")) return true;
  if (trimmed.startsWith("hsl(") || trimmed.startsWith("hsla(")) return true;
  if (trimmed.startsWith("var(--")) return true;
  return false;
};

const getSeriesColor = (color: string | undefined, idx: number) => {
  const fallback = COLORS[idx % COLORS.length];
  if (isValidColor(color)) {
    const trimmed = color!.trim();
    if (trimmed.startsWith("var(--") && !trimmed.includes(",")) {
      return `${trimmed.replace(/\)$/, "")}, ${fallback})`;
    }
    return trimmed;
  }
  return fallback;
};

const coerceNumber = (value: unknown) => {
  if (typeof value === "number") return Number.isFinite(value) ? roundChartNumber(value) : value;
  if (value === null || value === undefined) return value;
  if (typeof value !== "string") return value;

  const trimmed = value.replace(/\s+/g, " ").trim();
  if (!trimmed) return value;

  // Remove currency symbols and non-numeric characters except separators and minus
  let cleaned = trimmed.replace(/[^\d.,-]/g, "");
  if (!cleaned) return value;

  // Handle common NL format: 1.234,56 -> 1234.56
  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");
  if (hasComma && hasDot) {
    // Assume dots are thousands separators and comma is decimal
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (hasComma && !hasDot) {
    // Assume comma is decimal separator
    cleaned = cleaned.replace(",", ".");
  } else if (hasDot && !hasComma) {
    // Assume dot is decimal separator, keep as-is
  }

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? roundChartNumber(parsed) : value;
};

const ChartRenderer = ({ chartSpec, hideTitle = false, fillHeight = false }: ChartRendererProps) => {
  const { chart_type, x_field, series, data, title, description } = chartSpec;
  const chartId = useId().replace(/:/g, "");

  // Normalize a key for fuzzy lookup: lowercase + collapse spaces/underscores/hyphens.
  const normalizeForLookup = (k: string) => k.toLowerCase().replace(/[\s_-]+/g, "");

  // Build a normalized → actual-key map so x_field and data_key matching is case/spacing-insensitive.
  const keyMap: Record<string, string> = {};
  if (Array.isArray(data) && data.length > 0 && data[0] && typeof data[0] === "object") {
    for (const key of Object.keys(data[0] as Record<string, unknown>)) {
      keyMap[normalizeForLookup(key)] = key;
    }
  }
  const resolveKey = (k: string) => {
    const resolved = keyMap[normalizeForLookup(k)] ?? k;
    if (resolved === k && data.length > 0 && !(k in (data[0] as Record<string, unknown>))) {
      console.warn(`[ChartRenderer] key "${k}" not found in data. Available keys:`, Object.keys(data[0] as Record<string, unknown>));
    }
    return resolved;
  };

  const normalizedXField = resolveKey(x_field);
  const normalizedSeries = series.map((s) => ({ ...s, data_key: resolveKey(s.data_key) }));

  const normalizedData = Array.isArray(data)
    ? data
        .map((row) => {
        if (!row || typeof row !== "object") return row;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
        const next: Record<string, any> = { ...(row as Record<string, any>) };
        normalizedSeries.forEach((s) => {
          const key = s.data_key;
          if (key && key in next) {
            next[key] = coerceNumber(next[key]);
          }
        });
        return next;
      })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
        .filter((row) => row && typeof row === "object" && normalizedXField in (row as Record<string, any>))
    : data;

  const getSeriesConfig = (dataKey?: string) => normalizedSeries.find((seriesItem) => seriesItem.data_key === dataKey);

  const formatNumeric = (value: number, minimumFractionDigits = 0) =>
    new Intl.NumberFormat("nl-NL", {
      minimumFractionDigits,
      maximumFractionDigits: 6,
    }).format(roundChartNumber(value));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  const formatValue = (value: any, format?: string) => {
    const numericValue = typeof value === "number" ? roundChartNumber(value) : value;

    if (typeof numericValue !== "number" || !Number.isFinite(numericValue)) {
      return value;
    }
    if (format === "percentage") {
      return `${formatNumeric(numericValue * 100)}%`;
    }
    if (format === "euro" || format === "currency") {
      return `€${formatNumeric(numericValue, 2)}`;
    }
    if (format === "hours") {
      return `${formatNumeric(numericValue)} uur`;
    }
    return formatNumeric(numericValue);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
  const tooltipFormatter = (value: any, name: string, item: { dataKey?: string | number }) => {
    const dataKey = typeof item?.dataKey === "string" ? item.dataKey : undefined;
    const seriesConfig = getSeriesConfig(dataKey);
    return [
      formatValue(coerceNumber(value), seriesConfig?.value_format || chartSpec.value_format),
      seriesConfig?.label || name,
    ];
  };

  const renderCartesianFrame = () => (
    <>
      <CartesianGrid vertical={false} stroke={GRID_COLOR} />
      <XAxis
        dataKey={normalizedXField}
        axisLine={false}
        tickLine={false}
        tick={AXIS_TICK_STYLE}
        tickMargin={8}
      />
      <YAxis
        axisLine={false}
        tickLine={false}
        tick={AXIS_TICK_STYLE}
        tickMargin={8}
        width={80}
        tickFormatter={(value) => formatValue(coerceNumber(value), chartSpec.value_format)}
      />
      <Tooltip
        formatter={tooltipFormatter}
        contentStyle={TOOLTIP_STYLE}
        cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
        labelStyle={{ color: "#0f172a", fontWeight: 600, marginBottom: 4 }}
      />
      <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12, color: "#475569" }} iconType="circle" />
    </>
  );

  const renderChart = () => {
    switch (chart_type) {
      case "bar":
        return (
          <BarChart data={normalizedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            {renderCartesianFrame()}
            {normalizedSeries.map((s, idx) => (
              <Bar
                key={s.data_key}
                dataKey={s.data_key}
                name={s.label || s.data_key}
                fill={getSeriesColor(s.color, idx)}
                fillOpacity={0.85}
                radius={[6, 6, 0, 0]}
                maxBarSize={42}
              />
            ))}
          </BarChart>
        );

      case "line":
        return (
          <LineChart data={normalizedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            {renderCartesianFrame()}
            {normalizedSeries.map((s, idx) => {
              const color = getSeriesColor(s.color, idx);
              return (
                <Line
                  key={s.data_key}
                  type="monotone"
                  dataKey={s.data_key}
                  name={s.label || s.data_key}
                  stroke={color}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4, fill: color, stroke: "#ffffff", strokeWidth: 2 }}
                  connectNulls
                  isAnimationActive={false}
                />
              );
            })}
          </LineChart>
        );

      case "area":
        return (
          <AreaChart data={normalizedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <defs>
              {normalizedSeries.map((s, idx) => {
                const color = getSeriesColor(s.color, idx);
                return (
                  <linearGradient key={s.data_key} id={`${chartId}-${s.data_key}-gradient`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={color} stopOpacity={0.04} />
                  </linearGradient>
                );
              })}
            </defs>
            {renderCartesianFrame()}
            {normalizedSeries.map((s, idx) => {
              const color = getSeriesColor(s.color, idx);
              return (
                <Area
                  key={s.data_key}
                  type="monotone"
                  dataKey={s.data_key}
                  name={s.label || s.data_key}
                  stroke={color}
                  fill={`url(#${chartId}-${s.data_key}-gradient)`}
                  fillOpacity={1}
                  strokeWidth={2.25}
                  connectNulls
                />
              );
            })}
          </AreaChart>
        );

      case "pie": {
        if (normalizedSeries.length === 0) return null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- type is not narrowed here; typing it is a separate change
        const pieData = normalizedData.map((item: any) => ({
          name: item[normalizedXField],
          value: coerceNumber(item[normalizedSeries[0].data_key]),
        }));
        return (
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={84}
              paddingAngle={2}
              stroke="#ffffff"
              strokeWidth={2}
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => formatValue(coerceNumber(value), normalizedSeries[0]?.value_format || chartSpec.value_format)}
              contentStyle={TOOLTIP_STYLE}
              labelStyle={{ color: "#0f172a", fontWeight: 600, marginBottom: 4 }}
            />
            <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12, color: "#475569" }} iconType="circle" />
          </PieChart>
        );
      }

      case "radar":
        return (
          <RadarChart data={normalizedData}>
            <PolarGrid stroke={GRID_COLOR} />
            <PolarAngleAxis dataKey={normalizedXField} tick={{ fill: "#64748b", fontSize: 12 }} />
            <PolarRadiusAxis tick={{ fill: "#64748b", fontSize: 11 }} />
            {normalizedSeries.map((s, idx) => (
              <Radar
                key={s.data_key}
                name={s.label || s.data_key}
                dataKey={s.data_key}
                stroke={getSeriesColor(s.color, idx)}
                fill={getSeriesColor(s.color, idx)}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            ))}
            <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12, color: "#475569" }} iconType="circle" />
            <Tooltip formatter={tooltipFormatter} contentStyle={TOOLTIP_STYLE} />
          </RadarChart>
        );

      case "mixed": {
        const barSeries = normalizedSeries.filter((s) => !s.type || s.type === "bar");
        const lineSeries = normalizedSeries.filter((s) => s.type === "line");
        const areaSeries = normalizedSeries.filter((s) => s.type === "area");

        return (
          <div>
            {barSeries.length > 0 && (
              <div className="mb-4">
                <BarChart data={normalizedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  {renderCartesianFrame()}
                  {barSeries.map((s, idx) => (
                      <Bar
                        key={s.data_key}
                        dataKey={s.data_key}
                        name={s.label || s.data_key}
                        fill={getSeriesColor(s.color, idx)}
                        fillOpacity={0.85}
                        radius={[6, 6, 0, 0]}
                        maxBarSize={42}
                      />
                    ))}
                </BarChart>
              </div>
            )}
            {lineSeries.length > 0 && (
              <div className="mb-4">
                <LineChart data={normalizedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  {renderCartesianFrame()}
                  {lineSeries.map((s, idx) => {
                    const color = getSeriesColor(s.color, idx);
                    return (
                      <Line
                        key={s.data_key}
                        type="monotone"
                        dataKey={s.data_key}
                        name={s.label || s.data_key}
                        stroke={color}
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 4, fill: color, stroke: "#ffffff", strokeWidth: 2 }}
                        connectNulls
                        isAnimationActive={false}
                      />
                    );
                  })}
                </LineChart>
              </div>
            )}
            {areaSeries.length > 0 && (
              <AreaChart data={normalizedData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <defs>
                  {areaSeries.map((s, idx) => {
                    const color = getSeriesColor(s.color, idx);
                    return (
                      <linearGradient key={s.data_key} id={`${chartId}-${s.data_key}-mixed-gradient`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.04} />
                      </linearGradient>
                    );
                  })}
                </defs>
                {renderCartesianFrame()}
                {areaSeries.map((s, idx) => {
                  const color = getSeriesColor(s.color, idx);
                  return (
                    <Area
                      key={s.data_key}
                      type="monotone"
                      dataKey={s.data_key}
                      name={s.label || s.data_key}
                      stroke={color}
                      fill={`url(#${chartId}-${s.data_key}-mixed-gradient)`}
                      fillOpacity={1}
                      strokeWidth={2.25}
                      connectNulls
                    />
                  );
                })}
              </AreaChart>
            )}
          </div>
        );
      }

      default:
        return <div>Onbekend chart type: {chart_type}</div>;
    }
  };

  return (
    <div className={`w-full ${fillHeight ? "flex h-full min-h-0 flex-col" : ""}`}>
      {!hideTitle && title && <h3 className="mb-2 text-lg font-semibold text-slate-900">{title}</h3>}
      {!hideTitle && description && <p className="mb-4 text-sm leading-6 text-slate-600">{description}</p>}
      <div className={`w-full ${fillHeight ? "min-h-0 flex-1" : "h-64"}`}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartRenderer;
